import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown } from 'phosphor-react';

export function Navbar() {
  const { t } = useLanguage();
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const solutionsWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      const el = solutionsWrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setIsSolutionsOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const close = useCallback(() => setIsSolutionsOpen(false), []);

  const solutionsGroups = useMemo(
    () => ({
      platform: [
        { label: 'Construir', to: '/register' },
        { label: 'Lançar', to: '/register' },
        { label: 'Escalar', to: '/register' },
      ],
      capabilities: [
        { label: 'Agentes', to: '/register' },
        { label: 'Builder de apps', to: '/register' },
        { label: 'Primitivos de IA', to: '/register' },
        { label: 'Workflows', to: '/register' },
        { label: 'Apps mobile', to: '/register' },
      ],
    }),
    [],
  );

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 px-6 py-4 backdrop-blur-md bg-gradient-to-b from-white/80 to-transparent dark:from-[#09090B]/80"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-12">
          <Link to="/" className="flex items-center">
            <img src="/logo.svg" alt="Paladino AI" className="h-8 dark:hidden" />
            <img src="/logo-dark.svg" alt="Paladino AI" className="h-8 hidden dark:block" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <div
              ref={solutionsWrapRef}
              className="relative"
              onMouseEnter={() => setIsSolutionsOpen(true)}
              onMouseLeave={() => setIsSolutionsOpen(false)}
            >
              <button
                type="button"
                onClick={() => setIsSolutionsOpen((v) => !v)}
                className={[
                  'h-9 px-3 rounded-xl text-sm font-medium inline-flex items-center gap-2 transition-all duration-200 border',
                  'border-transparent hover:border-[color:color-mix(in_oklab,var(--card-border)_70%,transparent)]',
                  'text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)]',
                  isSolutionsOpen
                    ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_90%,var(--nt-primary)_10%)] border-[color:color-mix(in_oklab,var(--card-border)_70%,var(--nt-primary)_30%)]'
                    : '',
                ].join(' ')}
                aria-expanded={isSolutionsOpen}
                aria-haspopup="menu"
              >
                <span>Soluções</span>
                <CaretDown
                  size={16}
                  className={[
                    'transition-transform duration-150 text-[color:var(--foreground-muted)]',
                    isSolutionsOpen ? 'rotate-180' : '',
                  ].join(' ')}
                />
              </button>

              <AnimatePresence>
                {isSolutionsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.985 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute left-0 top-full mt-3 w-[860px] max-w-[calc(100vw-3rem)]"
                    style={{ zIndex: 50 }}
                    role="menu"
                  >
                    <div className="relative rounded-3xl border theme-border shadow-2xl shadow-black/20 overflow-hidden bg-[color:color-mix(in_oklab,var(--background-secondary)_92%,var(--foreground)_8%)]">
                      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(500px_220px_at_18%_0%,color-mix(in_oklab,var(--nt-primary)_14%,transparent),transparent_70%)]" />
                      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(500px_220px_at_82%_0%,color-mix(in_oklab,var(--brand-400)_12%,transparent),transparent_70%)]" />
                      <div className="relative grid grid-cols-3">
                        <div className="p-7">
                          <div className="inline-flex items-center rounded-full border theme-border px-2.5 py-1 text-[10px] font-semibold tracking-[0.14em] uppercase theme-text-muted">
                            Soluções
                          </div>
                          <div className="text-[26px] mt-4 leading-tight font-semibold tracking-tight theme-text">
                            AppGen
                          </div>
                          <div className="mt-2 text-sm theme-text-muted leading-relaxed max-w-[18rem]">
                            Gere apps e fluxos que já nascem conectados aos seus dados, com governança e segurança.
                          </div>
                        </div>

                        <div className="p-7 border-l theme-border">
                          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase theme-text-muted mb-4">
                            Plataforma
                          </div>
                          <div className="flex flex-col gap-2">
                            {solutionsGroups.platform.map((it) => (
                              <Link
                                key={it.label}
                                to={it.to}
                                onClick={close}
                                className="group px-3 py-2 rounded-2xl text-[22px] leading-tight font-medium theme-text hover:text-[color:var(--nt-primary)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_84%,transparent)] transition-all duration-200"
                              >
                                <span className="inline-flex items-center gap-2">
                                  {it.label}
                                  <span className="text-xs opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">→</span>
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>

                        <div className="p-7 border-l theme-border">
                          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase theme-text-muted mb-4">
                            Capacidades
                          </div>
                          <div className="flex flex-col gap-2">
                            {solutionsGroups.capabilities.map((it) => (
                              <Link
                                key={it.label}
                                to={it.to}
                                onClick={close}
                                className="group px-3 py-2 rounded-2xl text-[20px] leading-tight font-medium theme-text hover:text-[color:var(--nt-primary)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_84%,transparent)] transition-all duration-200"
                              >
                                <span className="inline-flex items-center gap-2">
                                  {it.label}
                                  <span className="text-xs opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">→</span>
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/login" className="text-sm transition-colors text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)]">
            {t('nav.login')}
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center justify-center h-9 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer bg-[color:var(--nt-primary)] text-white fill-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top),0_4px_12px_-2px_var(--nt-primary)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover),0_4px_12px_-2px_var(--nt-primary)]"
            style={{ boxShadow: 'inset 0 -0.125rem 0 0 var(--nt-primary-inset-bottom), inset 0 0.125rem 0 0 var(--nt-primary-inset-top), 0 4px 12px -2px rgba(139, 92, 246, 0.25)' }}
          >
            {t('nav.getStarted')}
          </Link>
        </div>
      </div>
    </motion.nav>
  );
}
